/* Team & Department rollup — the pure derivation behind the Team screen
 * (#/team, rail item 03, views/team.ts). A MODEST FACTUAL rollup (anatomy §2.6
 * + export S10): per-member workload counts resolved from the SAME task ledger
 * My Work reads, grouped by the task's `assignee_user_id`. NOT a priority
 * engine, not org-chart art.
 *
 * Honesty rules (build constitution): a member with no ledger tasks reads "no
 * indexed work", never a fabricated zero-that-looks-like-health; a free-text
 * assignee that resolves to no user id is attributable to nobody (it never
 * invents a person). Only real /users rows are members — team structure,
 * capacity and utilization are NOT derivable and render "not tracked" in the
 * view.
 *
 * Every function here is PURE (unit-tested); the fetch that feeds it lives in
 * lib/rosterTasks.ts. `today` is injectable so the overdue derivation is
 * deterministic under test.
 */
import type { AuthUser } from './auth.ts';

/** The subset of a `/clients/{slug}/tasks` row this rollup reads (the enriched
 * task shape carries far more; we ignore the rest). */
export interface RosterTask {
  status: string;
  assignee_user_id: number | null;
  due_date: string | null;
  client_slug: string;
}

/** The non-terminal task states (mirrors the server's `_OPEN_STATES`). */
export const OPEN_STATES = new Set([
  'open', 'in-progress', 'waiting-client', 'waiting-platform', 'implemented', 'regressed',
]);
const WAITING_STATES = new Set(['waiting-client', 'waiting-platform']);

export interface MemberRollup {
  user: AuthUser;
  /** open (non-terminal) tasks assigned to this member. */
  open: number;
  /** of those, waiting on an external actor (client/platform). */
  waiting: number;
  /** of those, past their connected due date. */
  overdue: number;
  /** evidence-verified tasks (terminal `verified`). */
  verified: number;
  /** distinct client slugs this member holds ANY task on (coverage). */
  clients: string[];
  /** true iff the member holds no tasks at all in the indexed ledgers. */
  noWork: boolean;
}

/** Today as an ISO date (YYYY-MM-DD), for the overdue comparison — matches the
 * server's `due_date < today` string compare. */
function isoToday(now = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** Roll every roster task up to its assigned user. Tasks with no
 * `assignee_user_id` (free-text or unassigned) belong to no member and are
 * dropped from per-member counts — they can never invent a person. Members with
 * no tasks still appear (with `noWork: true`), because the Team view lists every
 * real user, honestly. Pure. */
export function rollupMembers(
  users: readonly AuthUser[],
  tasks: readonly RosterTask[],
  now = new Date(),
): MemberRollup[] {
  const today = isoToday(now);
  const byUser = new Map<number, RosterTask[]>();
  for (const t of tasks) {
    const uid = t.assignee_user_id;
    if (uid == null) continue; // unattributable — never guessed onto a person
    const arr = byUser.get(uid);
    if (arr) arr.push(t); else byUser.set(uid, [t]);
  }
  return users.map((u) => {
    const mine = byUser.get(u.id) ?? [];
    const openTasks = mine.filter((t) => OPEN_STATES.has(t.status));
    const clients = [...new Set(mine.map((t) => t.client_slug))].sort();
    return {
      user: u,
      open: openTasks.length,
      waiting: openTasks.filter((t) => WAITING_STATES.has(t.status)).length,
      overdue: openTasks.filter((t) => t.due_date != null && t.due_date < today).length,
      verified: mine.filter((t) => t.status === 'verified').length,
      clients,
      noWork: mine.length === 0,
    };
  });
}

/** Department-wide honest totals (only cells derivable from the ledger). */
export interface TeamTotals {
  members: number;
  clientsCovered: number;
  open: number;
  waiting: number;
  overdue: number;
  verified: number;
}

export function teamTotals(rollups: readonly MemberRollup[]): TeamTotals {
  const covered = new Set<string>();
  let open = 0, waiting = 0, overdue = 0, verified = 0;
  for (const r of rollups) {
    open += r.open; waiting += r.waiting; overdue += r.overdue; verified += r.verified;
    for (const c of r.clients) covered.add(c);
  }
  return { members: rollups.length, clientsCovered: covered.size, open, waiting, overdue, verified };
}

/** Whole-roster ledger totals (FIX-audit F14). The per-member rollup counts only
 * tasks LINKED to a registered user id; legacy assignees are free-text name
 * strings with no user id, so a dept summed from members alone reads "0 open ·
 * nothing open" while the client book carries dozens of open tasks — a false
 * negative about the department. These totals count the WHOLE ledger truthfully
 * and expose the linkage gap (how many open tasks are attributable to a
 * registered member) so the strip never claims "nothing open" when work exists. */
export interface LedgerTotals {
  members: number;
  /** clients with ANY task in the ledger (not just members' linked ones). */
  clientsCovered: number;
  open: number;
  waiting: number;
  overdue: number;
  verified: number;
  /** of `open`, how many are assigned to a registered /users member. */
  linkedOpen: number;
  /** of `open`, how many are assigned to nobody / an unlinked legacy name. */
  unlinkedOpen: number;
}

export function ledgerTotals(
  users: readonly AuthUser[],
  tasks: readonly RosterTask[],
  now = new Date(),
): LedgerTotals {
  const today = isoToday(now);
  const userIds = new Set(users.map((u) => u.id));
  const covered = new Set<string>();
  let open = 0, waiting = 0, overdue = 0, verified = 0, linkedOpen = 0;
  for (const t of tasks) {
    covered.add(t.client_slug);
    if (OPEN_STATES.has(t.status)) {
      open++;
      if (WAITING_STATES.has(t.status)) waiting++;
      if (t.due_date != null && t.due_date < today) overdue++;
      if (t.assignee_user_id != null && userIds.has(t.assignee_user_id)) linkedOpen++;
    }
    if (t.status === 'verified') verified++;
  }
  return {
    members: users.length, clientsCovered: covered.size, open, waiting, overdue, verified,
    linkedOpen, unlinkedOpen: open - linkedOpen,
  };
}
